import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Mail,
  User,
  Calendar,
  Pencil,
  Save,
  Loader2,
  List,
  Building2,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import { useSidebar } from '../../contexts/SidebarContext';

interface SubscriberData {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  confirmed: boolean;
  unsubscribed: boolean;
  unsubscribed_at: string | null;
  created_at: string;
  updated_at: string;
  crm_contact_id: string | null;
  preferences: Record<string, any>;
  metadata: Record<string, any>;
}

interface SubscriberList {
  id: string;
  name: string;
}

interface CRMContactInfo {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  account_id: string | null;
  account_name: string | null;
}

interface SubscriberDetailSidebarProps {
  subscriberId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_CONFIG = {
  active: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  unsubscribed: { label: 'Desabonne', color: 'bg-red-100 text-red-700', icon: XCircle },
};

function getSubscriberStatus(sub: SubscriberData) {
  if (sub.unsubscribed) return 'unsubscribed';
  if (sub.confirmed) return 'active';
  return 'pending';
}

export function SubscriberDetailSidebar({
  subscriberId,
  isOpen,
  onClose,
}: SubscriberDetailSidebarProps) {
  const { openAccountSidebar } = useSidebar();
  const [subscriber, setSubscriber] = useState<SubscriberData | null>(null);
  const [subscriberLists, setSubscriberLists] = useState<SubscriberList[]>([]);
  const [crmContact, setCrmContact] = useState<CRMContactInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
  });

  useEffect(() => {
    if (!subscriberId || !isOpen) {
      setSubscriber(null);
      setSubscriberLists([]);
      setCrmContact(null);
      setIsEditing(false);
      return;
    }

    setLoading(true);

    // Fetch subscriber data
    const fetchSubscriber = async () => {
      const { data: sub } = await supabase
        .from('newsletter_subscribers')
        .select('*')
        .eq('id', subscriberId)
        .single();

      if (!sub) {
        setLoading(false);
        return;
      }

      // Normalize nullable DB columns to the non-nullable shape used by the UI.
      setSubscriber({
        id: sub.id,
        email: sub.email,
        first_name: sub.first_name,
        last_name: sub.last_name,
        confirmed: sub.confirmed ?? false,
        unsubscribed: sub.unsubscribed ?? false,
        unsubscribed_at: sub.unsubscribed_at,
        created_at: sub.created_at,
        updated_at: sub.updated_at,
        crm_contact_id: sub.crm_contact_id,
        preferences: (sub.preferences as Record<string, any>) ?? {},
        metadata: (sub.metadata as Record<string, any>) ?? {},
      });

      // Fetch lists
      const { data: listSubs } = await supabase
        .from('newsletter_list_subscriptions')
        .select('list_id')
        .eq('subscriber_id', subscriberId);

      if (listSubs && listSubs.length > 0) {
        const listIds = listSubs.map(ls => ls.list_id);
        const { data: listsData } = await supabase
          .from('newsletter_subscriber_lists')
          .select('id, name')
          .in('id', listIds);
        setSubscriberLists(listsData || []);
      } else {
        setSubscriberLists([]);
      }

      // Fetch CRM contact if linked
      if (sub.crm_contact_id) {
        const { data: contact } = await supabase
          .from('crm_contacts')
          .select('id, first_name, last_name, email, account_id')
          .eq('id', sub.crm_contact_id)
          .single();

        if (contact) {
          let account_name: string | null = null;
          if (contact.account_id) {
            const { data: account } = await supabase
              .from('crm_accounts')
              .select('name')
              .eq('id', contact.account_id)
              .single();
            account_name = account?.name || null;
          }
          setCrmContact({
            ...contact,
            account_name,
          });
        } else {
          setCrmContact(null);
        }
      } else {
        setCrmContact(null);
      }

      setLoading(false);
    };

    fetchSubscriber();
  }, [subscriberId, isOpen]);

  const handleStartEditing = () => {
    if (!subscriber) return;
    setEditForm({
      first_name: subscriber.first_name || '',
      last_name: subscriber.last_name || '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!subscriber) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .update({
          first_name: editForm.first_name || null,
          last_name: editForm.last_name || null,
        })
        .eq('id', subscriber.id);

      if (error) throw error;

      setSubscriber({
        ...subscriber,
        first_name: editForm.first_name || null,
        last_name: editForm.last_name || null,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving subscriber:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!subscriberId) return null;

  const status = subscriber ? getSubscriberStatus(subscriber) : 'pending';
  const statusConfig = STATUS_CONFIG[status];
  const StatusIcon = statusConfig.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/30 z-40"
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="absolute right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex-shrink-0 border-b border-neutral-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Mail className="w-5 h-5 text-[#0066FF]" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-semibold text-neutral-900 truncate">
                      {subscriber?.email || 'Chargement...'}
                    </h2>
                    {subscriber && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${statusConfig.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0066FF] text-white rounded-lg text-sm hover:bg-[#0052CC] disabled:opacity-50"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Enregistrer
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleStartEditing}
                        className="p-2 text-neutral-400 hover:text-[#0066FF] hover:bg-blue-50 rounded-lg"
                        title="Modifier"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={onClose}
                        className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-[#0066FF] animate-spin" />
                </div>
              ) : subscriber ? (
                <>
                  {/* Informations */}
                  <div className="bg-neutral-50 rounded-xl p-4 space-y-3">
                    <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Informations</h3>
                    {isEditing ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-neutral-500 mb-1">Prenom</label>
                          <input
                            type="text"
                            value={editForm.first_name}
                            onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                            className="w-full px-2.5 py-1.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
                            placeholder="Prenom"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-neutral-500 mb-1">Nom</label>
                          <input
                            type="text"
                            value={editForm.last_name}
                            onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                            className="w-full px-2.5 py-1.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
                            placeholder="Nom"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        <div className="flex items-center gap-2.5">
                          <Mail className="w-4 h-4 text-neutral-400 shrink-0" />
                          <span className="text-sm text-neutral-700">{subscriber.email}</span>
                        </div>
                        {(subscriber.first_name || subscriber.last_name) && (
                          <div className="flex items-center gap-2.5">
                            <User className="w-4 h-4 text-neutral-400 shrink-0" />
                            <span className="text-sm text-neutral-700">
                              {[subscriber.first_name, subscriber.last_name].filter(Boolean).join(' ')}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2.5">
                          <Calendar className="w-4 h-4 text-neutral-400 shrink-0" />
                          <span className="text-sm text-neutral-700">
                            Inscrit le {format(new Date(subscriber.created_at), 'dd MMM yyyy', { locale: fr })}
                          </span>
                        </div>
                        {subscriber.unsubscribed && subscriber.unsubscribed_at && (
                          <div className="flex items-center gap-2.5">
                            <XCircle className="w-4 h-4 text-red-400 shrink-0" />
                            <span className="text-sm text-red-600">
                              Desabonne le {format(new Date(subscriber.unsubscribed_at), 'dd MMM yyyy', { locale: fr })}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Listes */}
                  <div>
                    <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <List className="w-3.5 h-3.5" /> Listes ({subscriberLists.length})
                    </h3>
                    {subscriberLists.length === 0 ? (
                      <p className="text-sm text-neutral-400 italic">Aucune liste</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {subscriberLists.map(list => (
                          <span
                            key={list.id}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-[#0052CC]"
                          >
                            {list.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Contact CRM */}
                  <div>
                    <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5" /> Contact CRM
                    </h3>
                    {crmContact ? (
                      <div className="space-y-2">
                        <Link
                          to={`/admin/crm/contacts/${crmContact.id}`}
                          onClick={onClose}
                          className="flex items-center gap-2.5 p-2.5 bg-neutral-50 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-[#0066FF] text-xs font-medium">
                            {crmContact.first_name[0]}{crmContact.last_name[0]}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-neutral-900 truncate">
                              {crmContact.first_name} {crmContact.last_name}
                            </p>
                            {crmContact.email && (
                              <p className="text-xs text-neutral-500 truncate">{crmContact.email}</p>
                            )}
                          </div>
                        </Link>
                        {crmContact.account_id && crmContact.account_name && (
                          <button
                            onClick={() => {
                              onClose();
                              setTimeout(() => openAccountSidebar(crmContact.account_id!), 100);
                            }}
                            className="flex items-center gap-2.5 p-2.5 bg-neutral-50 rounded-lg hover:bg-emerald-50 transition-colors w-full text-left"
                          >
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                              <Building2 className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-neutral-900 truncate">{crmContact.account_name}</p>
                              <p className="text-xs text-neutral-500">Voir le compte</p>
                            </div>
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-neutral-400 italic">Aucun contact CRM associe</p>
                    )}
                  </div>

                  {/* Preferences */}
                  {subscriber.preferences && Object.keys(subscriber.preferences).length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">Preferences</h3>
                      <div className="bg-neutral-50 rounded-lg p-3 space-y-1.5">
                        {subscriber.preferences.frequency && (
                          <div className="flex justify-between text-sm">
                            <span className="text-neutral-500">Frequence</span>
                            <span className="text-neutral-700 font-medium">
                              {subscriber.preferences.frequency === 'daily' ? 'Quotidien' :
                               subscriber.preferences.frequency === 'weekly' ? 'Hebdomadaire' :
                               subscriber.preferences.frequency === 'monthly' ? 'Mensuel' :
                               subscriber.preferences.frequency}
                            </span>
                          </div>
                        )}
                        {subscriber.preferences.format && (
                          <div className="flex justify-between text-sm">
                            <span className="text-neutral-500">Format</span>
                            <span className="text-neutral-700 font-medium uppercase">{subscriber.preferences.format}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Metadata */}
                  {subscriber.metadata && Object.keys(subscriber.metadata).length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">Metadonnees</h3>
                      <div className="bg-neutral-50 rounded-lg p-3 space-y-1.5">
                        {Object.entries(subscriber.metadata).map(([key, value]) => (
                          <div key={key} className="flex justify-between text-sm">
                            <span className="text-neutral-500">{key}</span>
                            <span className="text-neutral-700 font-medium truncate ml-4">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-neutral-200 px-6 py-3">
              <Link
                to="/admin/newsletter/subscribers"
                onClick={onClose}
                className="text-sm text-[#0066FF] hover:text-[#0066FF] font-medium"
              >
                Voir dans la page Abonnes
              </Link>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
